import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { PERSPECTIVES, PERSPECTIVE_BY_SLUG } from "@/data/perspectives";

/**
 * /perspectives/:slug — individual research paper detail page.
 */
const PerspectiveDetailPage = () => {
  const { slug } = useParams();
  const paper = PERSPECTIVE_BY_SLUG[slug];
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!paper) return <Navigate to="/perspectives" replace />;

  const idx = PERSPECTIVES.findIndex((p) => p.slug === slug);
  const next = PERSPECTIVES[(idx + 1) % PERSPECTIVES.length];

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.perspectiveDetailPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index={`Paper ${String(idx + 1).padStart(2, "0")}`}
          label={paper.tag}
          title={paper.title}
          description={paper.excerpt}
          breadcrumb={`Perspectives / ${paper.tag.split("·")[0].trim()}`}
        />

        {/* Body */}
        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-32 space-y-6">
                <div>
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                    Published
                  </div>
                  <div className="text-[14px] text-[var(--kr-text)]">
                    {paper.date}
                  </div>
                </div>
                <div>
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                    Reading time
                  </div>
                  <div className="text-[14px] text-[var(--kr-text)]">
                    {paper.minutes}
                  </div>
                </div>
                <div>
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                    Theme
                  </div>
                  <div className="text-[14px] text-[var(--kr-text)]">
                    {paper.tag}
                  </div>
                </div>
                {paper.contributor && (
                  <div data-testid="kr-perspective-contributor">
                    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                      Contributor
                    </div>
                    <Link
                      to={paper.contributor.href}
                      className="text-[14px] text-[var(--kr-text)] hover:text-[var(--kr-gold)] transition-colors"
                    >
                      {paper.contributor.name}
                    </Link>
                    <div className="text-[12px] text-[var(--kr-text-muted)] leading-[1.5] mt-1">
                      {paper.contributor.role}
                    </div>
                  </div>
                )}
                <div className="kr-rule" />
                <p className="text-[12px] text-[var(--kr-text-muted)] leading-[1.6] italic">
                  Written for sophisticated readers. Not investment advice.
                </p>
              </div>
            </aside>

            <article className="lg:col-span-9 lg:pl-8">
              <p className="font-serif-display italic text-[var(--kr-text)] text-[1.3rem] leading-[1.6] border-l-2 border-[var(--kr-gold)] pl-8 mb-12 max-w-[60ch]">
                {paper.excerpt}
              </p>

              {paper.body.map((para, i) => (
                <React.Fragment key={i}>
                  <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.95] mb-8 max-w-[68ch]">
                    {para}
                  </p>
                  {i === 1 && paper.pullquote && (
                    <blockquote className="my-12 py-10 border-y border-[var(--kr-line)]">
                      <p className="font-serif-display italic text-[var(--kr-text)] text-[1.6rem] lg:text-[1.85rem] leading-[1.3] max-w-[42ch] text-[var(--kr-gold)]">
                        &ldquo;{paper.pullquote}&rdquo;
                      </p>
                    </blockquote>
                  )}
                </React.Fragment>
              ))}

              <div className="kr-rule my-12" />
              <p className="text-[12px] text-[var(--kr-text-muted)] leading-[1.7] italic max-w-[60ch]">
                The views expressed in this paper are those of Kellermann Reid
                &amp; Co. at the time of writing. They are not a forecast and do
                not constitute investment, legal or tax advice. Readers must
                make their own assessment of the matters discussed.
              </p>
            </article>
          </div>
        </section>

        {/* CTA + Next */}
        <section className="border-t border-[var(--kr-line)] py-20 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <h3 className="font-serif-display text-[var(--kr-text)] text-[1.7rem] leading-[1.2] max-w-[36ch]">
                Subscribe to the partnership&apos;s{" "}
                <span className="italic text-[var(--kr-gold)]">
                  private notes
                </span>
                .
              </h3>
            </div>
            <div className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.perspectiveDetailRequest}
                className="kr-btn-primary"
              >
                Request Subscription
                <span aria-hidden>→</span>
              </button>
              <Link
                to={`/perspectives/${next.slug}`}
                data-testid={LANDING.perspectiveDetailNext}
                className="kr-btn-ghost"
              >
                Next paper →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default PerspectiveDetailPage;
